import React, { useEffect, useState } from "react";
import { ReportHandlerTypeProps } from "../ReportHandler";
import {
  Column,
  DATE_FORMAT_STR,
  DATE_FORMATTER_STR,
  DateRange,
  DateRangeInput,
  formatDate,
  getRawDate,
  isBefore,
  Row,
  Table,
  Typography,
  useLoading,
} from "best-common-react";
import { previousMonth, yesterday } from "../../../util/DateObjUtil";
import { RequestCountDTO, RequestsCountDTO } from "../../../types/RequestCounts";
import { getRequestCounts } from "../../../api/RequesTixApi";

const RequestCountTable = ({ requestsCount }: { requestsCount: RequestCountDTO }) => {
  return (
    <Table>
      <Table.Body>
        <Table.Row>
          <Table.TdInfo>Total</Table.TdInfo>
          <Table.Td>{requestsCount.total}</Table.Td>
        </Table.Row>
        <Table.Row>
          <Table.TdInfo>Open</Table.TdInfo>
          <Table.Td>{requestsCount.open}</Table.Td>
        </Table.Row>
        <Table.Row>
          <Table.TdInfo>In Review</Table.TdInfo>
          <Table.Td>{requestsCount.inReview}</Table.Td>
        </Table.Row>
        <Table.Row>
          <Table.TdInfo>Approved</Table.TdInfo>
          <Table.Td>{requestsCount.approved}</Table.Td>
        </Table.Row>
        <Table.Row>
          <Table.TdInfo>Denied</Table.TdInfo>
          <Table.Td>{requestsCount.denied}</Table.Td>
        </Table.Row>
        <Table.Row>
          <Table.TdInfo>Cancelled</Table.TdInfo>
          <Table.Td>{requestsCount.cancelled}</Table.Td>
        </Table.Row>
      </Table.Body>
    </Table>
  );
};

const RequestCountsReport = ({ reportData, setReportData, setCanExport }: ReportHandlerTypeProps) => {
  const { setLoading } = useLoading();
  const { startDate, endDate } = reportData;
  const [requestsCount, setRequestsCount] = useState<RequestsCountDTO | undefined>(undefined);

  const [dateRange, setDateRange] = useState<DateRange<Date>>({
    start: !!startDate ? getRawDate(startDate, DATE_FORMAT_STR) : previousMonth,
    end: !!endDate ? getRawDate(endDate, DATE_FORMAT_STR) : yesterday,
  });

  const fetchRequestCounts = async () => {
    try {
      setLoading(true);
      const requestsCount = await getRequestCounts(
        formatDate(dateRange.start, DATE_FORMATTER_STR),
        formatDate(dateRange.end, DATE_FORMATTER_STR),
      );
      setRequestsCount(requestsCount);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateDate = ({ start, end }: DateRange<Date>) => {
    if (!!start && !!end) {
      setReportData((prev) => ({
        ...prev,
        startDate: formatDate(start, DATE_FORMATTER_STR),
        endDate: formatDate(end, DATE_FORMATTER_STR),
      }));
    }
  };

  useEffect(() => {
    const newDate: DateRange<Date> = {
      start: startDate ? getRawDate(startDate, DATE_FORMAT_STR) : previousMonth,
      end: endDate ? getRawDate(endDate, DATE_FORMAT_STR) : yesterday,
    };
    if (!startDate || !endDate) {
      updateDate(newDate);
    }
    setDateRange(newDate);
    setCanExport(!!newDate.start && !!newDate.end && isBefore(newDate.start, newDate.end));
  }, [startDate, endDate]);

  useEffect(() => {
    fetchRequestCounts();
  }, [dateRange]);

  return (
    <>
      <Row>
        <Column width={4}>
          <DateRangeInput
            id="game-date"
            label="Game Date"
            value={dateRange}
            onChange={updateDate}
            withPortal
            gutterBottom
            popoverPlacement="bottom-end"
          />
        </Column>
      </Row>
      {!!requestsCount && (
        <Row className="bcr-mt-4">
          <Column width={3}>
            <Typography variant="titleLarge">Personal</Typography>
            <RequestCountTable requestsCount={requestsCount.personal} />
          </Column>
          <Column width={3}>
            <Typography variant="titleLarge">Business</Typography>
            <RequestCountTable requestsCount={requestsCount.business} />
          </Column>
          <Column width={3}>
            <Typography variant="titleLarge">Total</Typography>
            <RequestCountTable requestsCount={requestsCount.total} />
          </Column>
        </Row>
      )}
    </>
  );
};

export default RequestCountsReport;
